import React, { useEffect, useState } from 'react';
import api from '../services/api';

const STATUS_COLORS = {
  lead:         { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  verified:     { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  visa_approved:{ bg: '#fefce8', color: '#a16207', border: '#fef08a' },
  enrolled:     { bg: '#f0fdf4', color: '#166534', border: '#86efac' },
};

export default function ViewerReferralsPage() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filter, setFilter]       = useState('');
  const [actionLoading, setAL]    = useState(null);

  const load = async (status = '') => {
    setLoading(true); setError('');
    try {
      const params = status ? `?status=${status}` : '';
      const { data } = await api.get(`/referral/viewer/referrals${params}`);
      setReferrals(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load referrals');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleFilter = e => { setFilter(e.target.value); load(e.target.value); };

  const markVerified = async (id) => {
    setAL(id);
    try {
      await api.put(`/referral/admin/referrals/${id}/status`, { status: 'verified' });
      setReferrals(prev => prev.map(r => r.id === id ? { ...r, status: 'verified' } : r));
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update status'); }
    finally { setAL(null); }
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Referrals Overview</h1>
          <p style={S.sub}>{referrals.length} total record{referrals.length !== 1 ? 's' : ''}</p>
        </div>
        <select value={filter} onChange={handleFilter} style={S.select}>
          <option value="">All Statuses</option>
          <option value="lead">Lead</option>
          <option value="verified">Verified</option>
          <option value="visa_approved">Visa Approved</option>
          <option value="enrolled">Enrolled</option>
        </select>
      </div>

      {loading && <div style={S.info}>Loading…</div>}
      {error   && <div style={S.errBox}>{error}</div>}

      {!loading && !error && referrals.length === 0 && (
        <div style={S.empty}>No referrals found.</div>
      )}

      {!loading && referrals.length > 0 && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['#','Referrer','Referred','Status','Date','Action'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {referrals.map((r, i) => {
                const sc = STATUS_COLORS[r.status] || { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
                return (
                  <tr key={r.id} style={i % 2 === 0 ? S.rowEven : S.rowOdd}>
                    <td style={S.td}>{r.id}</td>
                    <td style={S.td}>
                      <div style={S.name}>{r.referrer_name || '—'}</div>
                      <div style={S.sub2}>{r.referrer_email}</div>
                    </td>
                    <td style={S.td}>
                      <div style={S.name}>{r.referred_name || '—'}</div>
                      <div style={S.sub2}>{r.referred_email}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={S.td}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={S.td}>
                      {r.status === 'lead'
                        ? <button
                            style={S.verifyBtn}
                            disabled={actionLoading === r.id}
                            onClick={() => markVerified(r.id)}
                          >
                            {actionLoading === r.id ? '…' : 'Mark Verified'}
                          </button>
                        : <span style={S.done}>✓ {r.status.replace('_',' ')}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  page:     { maxWidth: 1100, margin: '0 auto', padding: '2rem 1.25rem', fontFamily: "'Inter','Segoe UI',sans-serif" },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title:    { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  sub:      { color: '#64748b', fontSize: '.88rem', marginTop: '.25rem' },
  select:   { padding: '.5rem .9rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '.9rem', color: '#1e293b', background: '#fff', cursor: 'pointer' },
  info:     { textAlign: 'center', color: '#64748b', padding: '2rem' },
  errBox:   { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '.75rem 1rem', marginBottom: '1rem' },
  empty:    { textAlign: 'center', color: '#94a3b8', padding: '3rem', fontSize: '1rem' },
  tableWrap:{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,.05)' },
  table:    { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th:       { padding: '.75rem 1rem', textAlign: 'left', fontSize: '.78rem', fontWeight: 700, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '.05em' },
  td:       { padding: '.75rem 1rem', fontSize: '.88rem', color: '#1e293b', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' },
  rowEven:  { background: '#fff' },
  rowOdd:   { background: '#fafafa' },
  name:     { fontWeight: 600, color: '#0f172a' },
  sub2:     { fontSize: '.78rem', color: '#94a3b8', marginTop: '.1rem' },
  badge:    { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 700, textTransform: 'capitalize' },
  badgeRef: { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
  badgeSelf:{ display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' },
  verifyBtn:{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  done:     { fontSize: '.8rem', color: '#10b981', fontWeight: 600 },
};
